import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth, roleHome } from "../lib/auth";
import { Brand, Spinner } from "../components/Brand";

export default function ChangePassword() {
    const { user, refreshMe } = useAuth();
    const nav = useNavigate();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    if (user === undefined) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
                <Spinner />
            </div>
        );
    }
    if (!user) {
        nav("/login");
        return null;
    }

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (next.length < 8) {
            setErr("New password must be at least 8 characters.");
            return;
        }
        if (next !== confirm) {
            setErr("Passwords do not match.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Password changed");
            await refreshMe();
            nav(roleHome(user.role), { replace: true });
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] px-6 py-12">
            <div className="w-full max-w-md">
                <Brand />
                <div className="mt-8">
                    <div className="lx-label text-[#B2912A] mb-3">Security</div>
                    <h2 className="font-serif text-4xl text-[#180A21] mb-2">
                        Change your password.
                    </h2>
                    {user.must_change_password && (
                        <p className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 mb-4">
                            Your account has a temporary password. You must set a new one
                            before continuing.
                        </p>
                    )}
                </div>
                <form onSubmit={submit} className="space-y-5">
                    <div>
                        <label className="lx-label text-[#555] mb-2 block">
                            Current password
                        </label>
                        <input
                            data-testid="cpw-current"
                            type="password"
                            value={current}
                            onChange={(e) => setCurrent(e.target.value)}
                            required
                            className="w-full px-4 py-3 bg-white border border-[#E5E2DC] focus:border-[#D4AF37] focus:outline-none"
                        />
                    </div>
                    <div>
                        <label className="lx-label text-[#555] mb-2 block">
                            New password (min 8 chars)
                        </label>
                        <input
                            data-testid="cpw-new"
                            type="password"
                            value={next}
                            onChange={(e) => setNext(e.target.value)}
                            required
                            minLength={8}
                            className="w-full px-4 py-3 bg-white border border-[#E5E2DC] focus:border-[#D4AF37] focus:outline-none"
                        />
                    </div>
                    <div>
                        <label className="lx-label text-[#555] mb-2 block">
                            Confirm new password
                        </label>
                        <input
                            data-testid="cpw-confirm"
                            type="password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            required
                            className="w-full px-4 py-3 bg-white border border-[#E5E2DC] focus:border-[#D4AF37] focus:outline-none"
                        />
                    </div>
                    {err && (
                        <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3" data-testid="cpw-error">
                            {err}
                        </div>
                    )}
                    <button
                        data-testid="cpw-submit"
                        type="submit"
                        disabled={busy}
                        className="lx-btn-primary w-full py-3.5 font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    >
                        {busy ? <Spinner /> : "Update password →"}
                    </button>
                </form>
            </div>
        </div>
    );
}
